import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from 'context/AuthProvider';
import { SoundProvider } from 'context/SoundProvider';
import Layout from 'components/Layout';
import SessionLaunch from 'pages/SessionLaunch';
import SessionExpired from 'pages/SessionExpired';
import Home from 'pages/Home';
import Lobby from 'pages/Lobby';
import TablePage from 'pages/Table';
import PrivateRooms from 'pages/PrivateRooms';
import PrivateTablePage from 'pages/PrivateTable';
import Rules from 'pages/Rules';
import Transactions from 'pages/Transactions';
import HandHistory from 'pages/HandHistory';
import Terms from 'pages/Terms';
import Settings from 'pages/Settings';
import TableAssetPreview from 'pages/TableAssetPreview';
import {
  finishLaunchExchange,
  getToken,
  isLaunchExchangeInProgress,
  markInAppNavigation,
  SESSION_EXPIRED_EVENT,
  shouldSuppressSessionExpired,
} from 'lib/api';

function AuthLoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-casino-gold border-t-transparent" />
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { user, loading, getUserSnapshot, refreshSession } = useAuth();
  const location = useLocation();
  const sessionUser = user ?? getUserSnapshot();
  const launchPending = isLaunchExchangeInProgress() && !sessionUser;
  const recoveryAttempted = useRef(false);

  useEffect(() => {
    if (loading || launchPending || sessionUser || !getToken() || recoveryAttempted.current) return;
    recoveryAttempted.current = true;
    void refreshSession();
  }, [loading, launchPending, sessionUser, refreshSession]);

  if (loading || launchPending) {
    return <AuthLoadingScreen />;
  }

  if (!sessionUser) {
    if (getToken()) {
      return <AuthLoadingScreen />;
    }
    return <Navigate to="/session-expired" replace state={{ from: location }} />;
  }

  return children;
}

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialPathRef = useRef(true);

  useEffect(() => {
    if (initialPathRef.current) {
      initialPathRef.current = false;
      return;
    }
    markInAppNavigation();
  }, [location.pathname]);

  useEffect(() => {
    const onPopState = () => markInAppNavigation();
    window.addEventListener('popstate', onPopState, true);
    return () => window.removeEventListener('popstate', onPopState, true);
  }, []);

  useEffect(() => {
    const onExpired = (event: Event) => {
      const force = Boolean((event as CustomEvent<{ force?: boolean }>).detail?.force);
      if (!force && shouldSuppressSessionExpired()) return;
      navigate('/session-expired', { replace: true });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [navigate]);

  useEffect(() => {
    if (location.pathname === '/home' && getToken()) {
      finishLaunchExchange();
    }
  }, [location.pathname]);

  return (
    <Routes>
      <Route path="/" element={<SessionLaunch />} />
      <Route path="/session-expired" element={<SessionExpired />} />
      <Route
        path="/home"
        element={
          <RequireAuth>
            <Home />
          </RequireAuth>
        }
      />
      <Route
        path="/private"
        element={
          <RequireAuth>
            <PrivateRooms />
          </RequireAuth>
        }
      />
      <Route
        path="/private/:roomCode"
        element={
          <RequireAuth>
            <PrivateTablePage />
          </RequireAuth>
        }
      />
      <Route path="/table-asset" element={<TableAssetPreview />} />
      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route path="/lobby" element={<Lobby />} />
        <Route path="/table/:id" element={<TablePage />} />
        <Route path="/rules" element={<Rules />} />
        <Route path="/transactions" element={<Transactions />} />
        <Route path="/hand-history" element={<HandHistory />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <SoundProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </SoundProvider>
    </BrowserRouter>
  );
}
